import React, { useState, useRef, useEffect } from "react";
import { Typography, Space, Select, Switch, Empty } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, getElementAtEvent } from "react-chartjs-2";

const { Text } = Typography;
const { Option } = Select;

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Calculate nice step size for x-axis based on max value
const calculateStepSize = (maxValue) => {
  if (maxValue <= 5) return 1;
  const niceSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
  const targetTicks = 6;
  const rawStep = maxValue / targetTicks;
  return niceSteps.find(s => s >= rawStep) || Math.ceil(rawStep / 100) * 100;
};

const HorizontalBar = ({ locationChartRef, locationChartData, selectedFilter, selectedLocation, onLocationClick, onClearSelection, onLocationTypeChange, onToggleChange }) => {
  const [selectedTitleOption, setSelectedTitleOption] = useState("current");
  const [showCities, setShowCities] = useState(true);
  
  const titleOptions = [
    { value: "current", label: "Current Location" },
    { value: "permanent", label: "Permanent Location" },
  ];

  // Use API data only - no hardcoded fallback
  const locationBarData = locationChartData?.locationBarChart || [];
  
  // Extract labels and data from API response
  const labels = locationBarData.length > 0 
    ? locationBarData.map(item => item.location || "Unknown").filter(loc => loc !== "")
    : [];
  
  const dataValues = locationBarData.length > 0
    ? locationBarData.map(item => item.hired || 0)
    : [];

  // Calculate dynamic step size for x-axis
  const maxDataValue = Math.max(...dataValues, 0);
  const stepSize = calculateStepSize(maxDataValue);

  const data = {
    labels,
    datasets: [
      {
        label: "2025",
        data: dataValues,
        datalabels: { display: false },
        backgroundColor: "#a0dcfc",
        borderWidth: 0,
        barThickness: 11,
      },
    ],
  };

  const [selectedData, setSelectedData] = useState({
    label: selectedLocation || "None",
    value2025: selectedLocation ? data.datasets[0].data[labels.indexOf(selectedLocation)] : 0,
  });

  // Update selectedData when locationChartData changes
  useEffect(() => {
    if (selectedLocation && labels.includes(selectedLocation)) {
      const index = labels.indexOf(selectedLocation);
      setSelectedData({
        label: selectedLocation,
        value2025: data.datasets[0].data[index],
      });
    }
  }, [locationChartData, selectedLocation, labels, data.datasets[0].data]);

  // Get gender analysis data for selected location
  const getSelectedLocationGenderData = () => {
    if (!selectedLocation || !locationChartData?.locationBarChart) return null;
    const locationData = locationChartData.locationBarChart.find(
      item => {
        const itemLocation = item.location || "Unknown";
        return itemLocation === selectedLocation || 
               (selectedLocation === "Unknown" && (itemLocation === "Unknown" || itemLocation === "" || !itemLocation));
      }
    );
    return locationData?.locationGenderAnalysis || null;
  };

  const chartRef = locationChartRef || useRef(null);

  // Move options inside component to access selectedLocation prop
  const options = {
    maintainAspectRatio: false,
    indexAxis: "y",
    responsive: true,
    // layout: { padding: { left: 10 } }, // for external "Locations" label
    categoryPercentage: 0.9,
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      x: {
        stacked: false,
        grid: { display: true },
        title: { display: false },
        ticks: {
          stepSize: stepSize,
          callback: function(value) {
            return Math.round(value);
          }
        },
      },
      y: {
        stacked: false,
        grid: { display: false },
        title: { display: false },
        ticks: {
          color: (ctx) => (ctx.tick.label === selectedLocation ? "#435fe8" : "#666"),
          font: {
            weight: (ctx) => (ctx.tick.label === selectedLocation ? "bold" : "normal"),
          },
        },
        afterFit: (scale) => {
          scale.width = 120; // Fixed width for y-axis labels
        }
      },
    },
    plugins: {
      legend: { display: false },
      title: { display: false },
      afterLayout: (chart) => {
        // Force y-axis to have fixed width
        chart.scales.y.width = 120;
      },
    },
  };


  useEffect(() => {
    if (selectedLocation) {
      const index = data.labels.indexOf(selectedLocation);
      if (index !== -1) {
        setSelectedData({
          label: selectedLocation,
          value2025: data.datasets[0].data[index],
        });
      }
    }
  }, [selectedLocation, showCities]);

  // const scrollLeft = () => setScrollX((prev) => Math.max(prev - SCROLL_STEP, 0));
  // const scrollRight = () =>
  //   setScrollX((prev) => Math.min(prev + SCROLL_STEP, maxScroll));
  const handleTitleOptionChange = (value) => {
    setSelectedTitleOption(value);
    if (onLocationTypeChange) {
      // Convert string to boolean: "permanent" -> true, "current" -> false
      const isPermanent = value === "permanent";
      onLocationTypeChange(isPermanent);
    }
  };

  const handleToggleChange = (checked) => {
    setShowCities(checked);
    if (onToggleChange) {
      onToggleChange(checked);
    }
  };

  const onClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;
    const element = getElementAtEvent(chart, event);
    if (element && element.length > 0) {
      const { index } = element[0];
      setSelectedData({
        label: data.labels[index],
        value2025: data.datasets[0].data[index],
      });
      
      // Call parent's click handler
      if (onLocationClick) {
        onLocationClick(event, element);
      }
    }
  };

  const handleClearSelection = () => {
    if (onClearSelection) {
      onClearSelection();
    }
  };

  // Check if there's any location data
  const hasLocationData = locationBarData && locationBarData.length > 0 && 
    dataValues.some(value => value > 0);

  return (
    <div
      style={{
        width: "100%",
        padding: "8px 16px 16px 0",
        fontSize: 13,
        borderRadius: 8,
        height: 520,
        display: "flex",
        flexDirection: "column",
        position: "relative",       
      }}
    >
      {/* Title and Toggle Container */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
        {/* Title Dropdown */}
        <Select
          value={selectedTitleOption}
          onChange={handleTitleOptionChange}
          size="large"
          bordered={false}
          getPopupContainer={(triggerNode) => triggerNode.parentElement}
          style={{
            width: 400,
            height: 48,
            borderRadius: "10px",
            fontSize: 16,
            fontWeight: 500,
            border: "1px solid rgba(214, 219, 247, 1)",
            backgroundColor: "#fff",
          }}
        >
          {titleOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              Top 10 locations ({option.label})
            </Option>
          ))}
        </Select>

        {/* Toggle Switch */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch
            checked={showCities}
            onChange={handleToggleChange}
            style={{
              width: 39,
              height: 22,
              transform: "rotate(0deg)",
              opacity: 1,
              borderRadius: 20,
              backgroundColor: showCities ? "rgba(37, 99, 235, 1)" : "#ccc",
            }}
          />
          <Text style={{ fontSize: 14, color: "#1a2645", whiteSpace: "nowrap" }}>{showCities ? "Showing Cities" : "Showing States"}</Text>
        </div>
      </div>

      {hasLocationData ? (
        <>
      {/* Chart container */}
      <div
        style={{
          flexGrow: 1,
          width: "100%",
          marginTop: 12,
          overflow: "hidden",
          position: "relative",
          display: "flex",
        }}
      >
        {/* Locations Label - Inside Chart Container */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            paddingRight: "0px",
          }}
        >
          <Text
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#1a2645",
              transform: "rotate(-90deg)",
              whiteSpace: "nowrap",
            }}
          >
            Locations
          </Text>
        </div>
        
        <div
          style={{
            flexGrow: 1,
            height: "100%",
            position: "relative",
          }}
        >
          <Bar options={options} data={data} ref={chartRef} onClick={onClick} />
        </div>
      </div>

      {/* No. of students line + arrows */}
      <div
        style={{
          marginTop: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Text style={{ fontSize: 13, whiteSpace: "nowrap", color: "#1a2645", marginLeft: "70px", fontWeight: 800 }}>
          Hires
        </Text>

        {/* thin gray line */}
        <div
          style={{
            flexGrow: 0.99,
            height: 1,
            backgroundColor: "#d1d1d1",
            borderRadius: 1,
          }}
        ></div>

        {/* arrows */}
        {/* <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            size="small"
            onClick={scrollLeft}
            disabled={scrollX === 0}
            style={{ 
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
              padding: '4px',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              color: scrollX === 0 ? '#bfbfbf' : 'rgba(50, 73, 215, 1)',
            }}
          />
          <Button
            type="text"
            icon={<RightOutlined />}
            size="small"
            onClick={scrollRight}
            disabled={scrollX >= maxScroll}
            style={{ 
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #d9d9d9',
              padding: '4px',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              color: scrollX >= maxScroll ? '#bfbfbf' : 'rgba(50, 73, 215, 1)',
            }}
          />
        </div> */}
      </div>

      {/* Legend below the scroll line */}
      {/* <div
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          marginLeft: 4,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            backgroundColor: "#a0dcfc",
            borderRadius: "50%",
          }}
        />
        <Text style={{ fontSize: 12, color: "#1a2645" }}>2025</Text>
      {/* </div> */} 
      {/* Clear button above details box */}
      {onClearSelection && (
        <div style={{ display: 'flex', justifyContent: 'flex-end',  marginTop: '12px' }}>
          <Text
            style={{
              fontSize: '11px',
              color: selectedLocation ? 'red' : 'grey',
              cursor: 'pointer'
            }}
            onClick={handleClearSelection}
          >
            Clear
          </Text>
        </div>
      )}

      {/* Selected Info Box */}
      <div
        style={{
          marginTop: 12,
          backgroundColor: "#fff",
          border: selectedLocation ? "1px solid #435fe8" : "1px solid rgba(194, 194, 194, 1)",
          borderRadius: '8px',
          padding: '12px 16px',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {selectedLocation ? (
          <>
            <Text style={{ fontSize: 12 }}>
              Selected:{" "}
              <Text strong style={{ color: "#435fe8" }}>
                {selectedData.label}
              </Text>
            </Text>

            <Space size={4}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  backgroundColor: "#a0dcfc",
                  borderRadius: "50%",
                }}
              />
              <Text style={{ fontSize: 12 }}>{selectedData.value2025} Hires</Text>
            </Space>
          </>
        ) : (
          <Space size={8}>
            <svg width="18" height="21" viewBox="0 0 18 21" style={{ marginLeft: '2px' }}>
              <rect 
                x="10.05" y="3" width="4.022172927856445" height="12" fill="rgba(50, 73, 215, 1)"opacity="1"
              />
              <rect 
                x="10.05" y="17" width="4.022172927856445" height="4" fill="rgba(50, 73, 215, 1)" opacity="1"
              />
            </svg>
            <Text style={{ 
              color: '#262626',
              fontFamily: 'Plus Jakarta Sans',fontWeight: 400,fontSize: 14,lineHeight: '18px',letterSpacing: '0%'
            }}>
              Note: Click the graph for more details.
            </Text>
          </Space>
        )}
      </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexGrow: 1 }}>
          <Empty />
        </div>
      )}
    </div>
  );
};

export default HorizontalBar;
